import { buildContextoCobro, getCobroReal } from "./cobro/getCobroFactura";
import { client } from "./supabase";

// El CHECK de `movimientos_caja.categoria_pago` tiene 21 valores (NULL incluido). De esos 21, SOLO
// dos AFIRMAN la existencia de un documento fiscal vinculado: 'cobro_factura' (cobró una emitida) y
// 'pago_proveedor' (pagó una recibida) — el resto (pago_factura, pago_credito, cobro_a_cuenta,
// cobro_credito, sueldo, adelanto_sueldo, comision_empleado, bonificacion, gasto_recurrente,
// comision_edificio, costo_financiero, gasto_sin_factura, cotizaciones_previsionales, pago_impuesto,
// pago_banco, otro, caja_chica, compra_activo, pago_a_cuenta) es CAJA PURA (10.5): no espera ni
// necesita documento, así que su sola presencia en `movimientos_caja` ya resuelve el movimiento.
// R3.4: enumerar "caja pura" bucket por bucket es frágil — cada categoría nueva del CHECK quedaba
// afuera y sobrevivía solo por el fallback ciego `movimiento_caja_id` (caso real: `cobro_credito`,
// agregado al CHECK en la sesión de 2026-08-13 — docs/modulos/flujo-caja-v2.md/estado-cuenta.md — y
// nunca nombrado acá). Se invierte: se nombra la EXCEPCIÓN (con documento), todo lo demás cae a caja
// pura por default, sin lista para mantener.
const CATEGORIAS_CON_DOCUMENTO = new Set(["cobro_factura", "pago_proveedor"]);

// ─── Predicado de "movimiento pendiente de vincular" ──────────────────────
// FUENTE ÚNICA (regla 10.1): mismo predicado que tiñe la fila naranja y arma el badge
// por moneda del toggle CLP/USD en FlujoCajaV2, Y que suma el badge del tile "Caja" del
// Home (utils/badges.js). Refleja el fallthrough de renderBadgeMovimiento: un movimiento
// es "pendiente" solo si NINGUNA superficie de vínculo lo cubre.
//   - factoringMovIds: ids vinculados como adelanto/remanente/puente de factoring.
//   - pagosMovIds: ids vinculados a un pago de empleado.
//   - movsVinculadosLibro (Fase 3.1 + R3.4): ids con evento en el libro `aplicaciones_saldo` por
//     origen bancario, CUALQUIER documento_tipo del eje cobro o pago (sii_emitida/sii_recibida/
//     factura_externa/boleta_honorario). Es la ÚNICA fuente de "tiene documento vinculado" —
//     `cobro_factura`/`pago_proveedor` (las dos categorías de CATEGORIAS_CON_DOCUMENTO) ya NO
//     resuelven por sí solas: sin evento en el libro, un movimiento con esas categorías sigue
//     pendiente (correcto — antes esas dos ramas tapaban movimientos sin ningún documento real,
//     ver medición 3.4 §1: 40/42 "flips" de la medición original eran en realidad caja pura mal
//     clasificada por una derivada mal diseñada, no huecos reales de estas dos categorías).
// Todos los sets los arma cada consumidor con SU data (el componente desde state, badges.js
// desde su propio fetch) y se pasan por parámetro — la función queda pura.
export const isMovPendiente = (m, pagosMovIds, factoringMovIds = new Set(), movsVinculadosLibro = new Set()) => {
  if (factoringMovIds.has(m.id)) return false;
  if (pagosMovIds.has(m.id)) return false;
  if (m.estado === "ignorado") return false;
  if (movsVinculadosLibro.has(m.id)) return false;
  if (m.match_factura_tipo === "factura_externa") return false;
  // Caja pura (10.5): cualquier categoría presente que NO afirme documento fiscal resuelve el
  // movimiento por su sola existencia — sin enumerar cada bucket. `categoria_pago` NULL (con o sin
  // fila de caja) cae al libro de arriba, igual que antes.
  const cat = m.movimiento_caja?.categoria_pago;
  if (cat != null && !CATEGORIAS_CON_DOCUMENTO.has(cat)) return false;
  return true;
};

// ─── Dirección del movimiento (Ingreso / Egreso) ──────────────────────────
// FUENTE ÚNICA (regla 10.1): el MISMO criterio que separa los subtabs "Ingresos"/"Egresos"
// del panel Históricos (filtroHistorico) y que cuenta los badges de pendientes por dirección.
// La dirección es el campo `tipo` del movimiento bancario ('ingreso' | 'egreso'); no se deriva
// del signo del monto ni de ninguna otra heurística. Todo movimiento es ingreso XOR egreso.
export const esMovIngreso = (m) => m.tipo === "ingreso";
export const esMovEgreso = (m) => m.tipo === "egreso";

// ─── Queries ──────────────────────────────────────────────

export const getMovimientosBancarios = async (pais, filters = {}) => {
  let query = client
    .from("movimientos_bancarios")
    // B3 Fase B paso 2: el vínculo banco↔factura SII se trae SOLO por el puente
    // `movimiento_bancario_facturas` (factura_tipo + factura_id). Ya NO se embeben las facturas por
    // la FK directa (`factura_sii_emitida_id`/`_recibida_id`, en retiro) — los lectores resuelven
    // folio/razón/tipo_dte del puente vía sus mapas por id (regla 10.192/10.194).
    .select(
      `*,
      facturas_vinculadas:movimiento_bancario_facturas(factura_tipo, factura_id),
      factura_externa:facturas_externas(numero_documento, archivo_url),
      movimiento_caja:movimientos_caja!movimiento_bancario_id(categoria_pago, entidad_nombre, descripcion, cuenta_er)`
    )
    .limit(5000)
    .eq("pais", pais)
    .order("fecha", { ascending: false });

  if (filters.cuenta_bancaria_id) query = query.eq("cuenta_bancaria_id", filters.cuenta_bancaria_id);
  if (filters.estado) query = query.eq("estado", filters.estado);
  if (filters.tipo) query = query.eq("tipo", filters.tipo);
  if (filters.fechaDesde) query = query.gte("fecha", filters.fechaDesde);
  if (filters.fechaHasta) query = query.lte("fecha", filters.fechaHasta);
  if (filters.moneda) query = query.eq("moneda", filters.moneda);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const getMovimientosBancariosPendientes = async (pais) => getMovimientosBancarios(pais, { estado: "pendiente" });

// Devuelve sets de IDs de facturas que NO deberían ofrecerse en el modal de vinculación
// ni proyectarse como pendientes en los paneles "Por cobrar" / "Por pagar":
//   1. SII EMITIDAS: cobradas según `getCobroReal` — la lógica ÚNICA de cobro (MBF → factoring
//      liquidada → cuotas). B2/B3 / regla 10.192: el estado de cobro NO se lee de la FK directa cruda,
//      se resuelve por el chokepoint `getCobroReal`. Esto además saca de "Por cobrar" las emitidas de
//      factoring liquidada que las Fuentes viejas no veían (0 filas MBF por 10.193).
//   2. SII RECIBIDAS: pagadas por el puente MBF (Σ mbf.monto ≥ total) o por plan de cuotas todas pagadas.
//   3. Facturas EXTERNAS: tienen movimiento_bancario_id directo en la propia fila.
//
// IMPORTANTE (regla 10.224): el criterio de "factura de proveedor PAGADA" vive ÚNICAMENTE en el ledger
// FIFO (`construirLedgerProveedor` / `getSaldosPorFacturaProveedor` / `getSaldosRecibidasPorFactura`),
// que considera pagos a cuenta sin MBF + NC + saldo inicial + tolerancia de redondeo. El Set `recibidas`
// de acá es el criterio LIVIANO (solo MBF/cuotas) y quedó como fuente SOLO del fallback de recibidas
// SIN `proveedor_id` del modal (~2 casos que el ledger, que va por proveedor, no puede resolver). Toda
// recibida CON `proveedor_id` obtiene su estado/saldo del ledger, no de este Set — no reimplementar el
// criterio de "pagada" fuera del ledger. `montosAcumRecibidas` acompaña al Set para el saldo parcial de
// ese mismo fallback. `montosAcumEmitidas` (cobro) y `montosAcumBoletas` (boletas sin proveedor) son las
// otras dos salidas parciales vivas.
export const getFacturasYaVinculadas = async (pais) => {
  const [pagosRes, mbfRes, externasPagadasRes, facEmitidasRes, libroEmitidasRes] = await Promise.all([
    client.from("plan_pagos_facturas").select("factura_sii_recibida_id, estado").eq("pais", pais).range(0, 49999),
    client
      .from("movimiento_bancario_facturas")
      .select("factura_tipo, factura_id, monto, movimiento_bancario_id")
      .range(0, 49999),
    client.from("facturas_externas").select("id").eq("pais", pais).not("movimiento_bancario_id", "is", null),
    // Universo de emitidas del país: getCobroReal (wrapper de resolverSaldo) se evalúa sobre CADA una.
    // `moneda` la necesita el resolver para la tolerancia por moneda. 126 filas en Chile hoy → bloque, sin N+1.
    client.from("facturas_sii_emitidas").select("id, monto_total, moneda").eq("pais", pais).range(0, 49999),
    // R2.3: libro de cobro (`aplicaciones_saldo`, fuente única — regla 10.190). getCobroReal delega en
    // resolverSaldo sobre esto; reemplaza los insumos crudos MBF/factoring/cuotas de la resolución de cobro.
    client
      .from("aplicaciones_saldo")
      .select("documento_tipo, documento_id, via, monto_documento, moneda_origen, fecha_evento")
      .eq("documento_tipo", "sii_emitida")
      .range(0, 49999),
  ]);
  if (pagosRes.error) throw pagosRes.error;
  if (mbfRes.error) throw mbfRes.error;
  if (externasPagadasRes.error) throw externasPagadasRes.error;
  if (facEmitidasRes.error) throw facEmitidasRes.error;
  if (libroEmitidasRes.error) throw libroEmitidasRes.error;

  const externas = new Set((externasPagadasRes.data || []).map((r) => r.id));

  // Acumulados Σ mbf.monto por factura (moneda cruda). Se devuelven para que los consumidores
  // (VincularMovimientoModal) muestren el saldo restante de un cobro/pago parcial. `montosAcumRecibidas`
  // alimenta además el Set `recibidas` (fallback sin proveedor). (Suma en moneda cruda: para splits
  // cross-currency el monto en otra moneda no se convierte acá → dirección segura; el cuadre cambiario
  // fino lo hace el ledger.)
  const montosAcumRecibidas = new Map(); // factura_id → Σ mbf.monto
  const montosAcumEmitidas = new Map();
  const montosAcumBoletas = new Map(); // boleta_honorario_id → Σ mbf.monto (F2b)
  for (const r of mbfRes.data || []) {
    if (r.factura_tipo === "sii_recibida") {
      montosAcumRecibidas.set(r.factura_id, (montosAcumRecibidas.get(r.factura_id) || 0) + Number(r.monto || 0));
    }
    if (r.factura_tipo === "sii_emitida") {
      montosAcumEmitidas.set(r.factura_id, (montosAcumEmitidas.get(r.factura_id) || 0) + Number(r.monto || 0));
    }
    if (r.factura_tipo === "boleta_honorario") {
      montosAcumBoletas.set(r.factura_id, (montosAcumBoletas.get(r.factura_id) || 0) + Number(r.monto || 0));
    }
  }

  // ── EMITIDAS — cobro resuelto por `getCobroReal` (chokepoint único, regla 10.192) ────────────
  // R2.3: getCobroReal es un wrapper de `resolverSaldo` sobre el libro `aplicaciones_saldo` (fuente
  // única, regla 10.190). `buildContextoCobro` indexa el libro una sola vez → O(1)/O(k) por factura.
  const ctxCobro = buildContextoCobro({
    aplicacionesSaldo: libroEmitidasRes.data || [],
    facturasEmitidas: facEmitidasRes.data || [],
  });
  const emitidas = new Set();
  for (const f of facEmitidasRes.data || []) {
    if (getCobroReal(f.id, ctxCobro).cobrada) emitidas.add(f.id);
  }

  // ── RECIBIDAS — criterio liviano (puente MBF + cuotas), SOLO para el fallback sin proveedor_id ───
  // (regla 10.224: la "pagada" real de proveedor la resuelve el ledger; esto es el fallback MBF/cuotas).
  // Fetch liviano del monto_total solo de las recibidas con algún vínculo MBF.
  const recibidas = new Set();
  const idsRecConVinculo = [...montosAcumRecibidas.keys()];
  const montosRecibidas = new Map();
  if (idsRecConVinculo.length > 0) {
    const { data: facsRec, error: errRec } = await client
      .from("facturas_sii_recibidas")
      .select("id, monto_total")
      .in("id", idsRecConVinculo);
    if (errRec) throw errRec;
    for (const f of facsRec || []) montosRecibidas.set(f.id, Number(f.monto_total || 0));
  }

  // Fuente puente (MBF) — Σ mbf.monto ≥ monto_total (tolerancia 0.01). Las parciales quedan FUERA
  // del Set → siguen disponibles en el selector con su saldo restante.
  for (const [fid, acum] of montosAcumRecibidas) {
    const total = montosRecibidas.get(fid) || 0;
    if (total > 0 && acum >= total - 0.01) recibidas.add(fid);
  }

  // Fuente cuotas (plan de pagos) — recibida con todas las cuotas pagadas (sin pendientes).
  const acumular = (filas, fkField) => {
    const stats = {};
    for (const c of filas || []) {
      const fid = c[fkField];
      if (!stats[fid]) stats[fid] = { total: 0, pendientes: 0 };
      stats[fid].total++;
      if (c.estado === "pendiente") stats[fid].pendientes++;
    }
    return stats;
  };
  for (const [fid, { total, pendientes }] of Object.entries(acumular(pagosRes.data, "factura_sii_recibida_id"))) {
    if (total > 0 && pendientes === 0) recibidas.add(Number(fid));
  }

  return { emitidas, recibidas, externas, montosAcumRecibidas, montosAcumEmitidas, montosAcumBoletas };
};

export const getMovimientoBancarioById = async (id) => {
  const { data, error } = await client.from("movimientos_bancarios").select("*").eq("id", id).single();
  if (error) throw error;
  return data;
};

// ─── Importación (desde Edge Function clay-import) ────────

export const insertMovimientosBancarios = async (movimientos) => {
  if (!movimientos?.length) return { nuevos: 0, duplicados: 0 };

  const { data, error } = await client
    .from("movimientos_bancarios")
    .upsert(movimientos, { onConflict: "external_id", ignoreDuplicates: true })
    .select();

  if (error) throw error;
  const nuevos = data?.length || 0;
  return { nuevos, duplicados: movimientos.length - nuevos };
};

// ─── Matching (actualizar sugerencias) ────────────────────

export const actualizarMatch = async (id, matchData) => {
  const { data, error } = await client.from("movimientos_bancarios").update(matchData).eq("id", id).select();
  if (error) throw error;
  return data?.[0];
};

// ─── Confirmar / Ignorar ─────────────────────────────────

export const confirmarMovimiento = async (id, { movimiento_caja_id, procesado_por }) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "procesado",
      movimiento_caja_id,
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

export const ignorarMovimiento = async (id, procesado_por) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "ignorado",
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

export const confirmarMovimientosBulk = async (ids, procesado_por) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "procesado",
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .in("id", ids)
    .select();
  if (error) throw error;
  return data || [];
};

// ─── Cuentas bancarias ───────────────────────────────────

export const getCuentasBancarias = async (pais) => {
  const { data, error } = await client
    .from("cuentas_bancarias")
    .select("*, bancos(nombre)")
    .eq("pais", pais)
    .eq("habilitado", true)
    .order("moneda", { ascending: true });
  if (error) throw error;
  return data || [];
};

export const actualizarSaldoCuenta = async (id, saldo, fecha) => {
  const { error } = await client
    .from("cuentas_bancarias")
    .update({ saldo_actual: saldo, saldo_fecha: fecha, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
};

// ─── Resumen ─────────────────────────────────────────────

export const getResumenMovimientos = async (pais, fechaDesde, fechaHasta) => {
  const movimientos = await getMovimientosBancarios(pais, { fechaDesde, fechaHasta });

  const ingresos = movimientos.filter((m) => m.tipo === "ingreso");
  const egresos = movimientos.filter((m) => m.tipo === "egreso");
  const pendientes = movimientos.filter((m) => m.estado === "pendiente");

  return {
    totalIngresos: ingresos.reduce((sum, m) => sum + Number(m.monto), 0),
    totalEgresos: egresos.reduce((sum, m) => sum + Number(m.monto), 0),
    cantidadIngresos: ingresos.length,
    cantidadEgresos: egresos.length,
    pendientes: pendientes.length,
    total: movimientos.length,
  };
};
